import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import { DotsThreeVertical, SortAscending, SortDescending, PencilSimple, Archive, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import { ClientAvatar } from "./ClientAvatar";
import { TagPill } from "./TagPill";
import { PlatformLogos } from "./PlatformLogos";
import { getIndustryIcon } from "./IndustryPicker";
import { TableHeaderIcon } from "../shared/TableHeaderIcon";
import type { Client, SortOption } from "./types";

interface ClientTableViewProps {
  clients: Client[];
  canManageClients: boolean;
  isArchived?: boolean;
  onClientClick?: (client: Client) => void;
  onEditClick?: (client: Client) => void;
  onArchiveClick?: (client: Client) => void;
  onRestoreClick?: (client: Client) => void;
  onDeleteClick?: (client: Client) => void;
  restoreDisabled?: boolean;
  triggerRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
  /** Column ids the user has hidden (from the route, persisted to localStorage). */
  hiddenColumns?: string[];
  /** Retained for call-site compatibility; the Columns toolbar owns visibility. */
  onHideColumn?: (id: string) => void;
}

export function ClientTableView({
  clients,
  canManageClients,
  isArchived = false,
  onClientClick,
  onEditClick,
  onArchiveClick,
  onRestoreClick,
  onDeleteClick,
  restoreDisabled = false,
  triggerRefs,
  sortBy,
  onSortChange,
  hiddenColumns = [],
}: ClientTableViewProps) {
  const isHidden = (id: string) => hiddenColumns.includes(id);
  return (
    <div className={`overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs${isArchived ? " mt-6 opacity-60" : ""}`} style={{ border: "0.5px solid var(--hairline)" }}>
      <table className="app-page-table w-full text-sm" aria-label={isArchived ? "Archived client list" : "Client list"}>
        <thead className="relative bg-background">
          <tr>
            <th scope="col" className="text-left py-2 px-4 text-[13px] font-semibold text-foreground">
              {onSortChange ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                  onClick={() => onSortChange(sortBy === "name_asc" ? "name_desc" : "name_asc")}
                >
                  <TableHeaderIcon name="client" />
                  Client
                  {(sortBy === "name_asc" || sortBy === "name_desc") && (
                    sortBy === "name_asc"
                      ? <SortAscending size={18} />
                      : <SortDescending size={18} />
                  )}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <TableHeaderIcon name="client" />
                  Client
                </span>
              )}
            </th>
            {!isHidden("industry") && (
              <th scope="col" className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap">
                <span className="flex items-center gap-1">
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() => onSortChange(sortBy === "industry_asc" ? "industry_desc" : "industry_asc")}
                    >
                      <TableHeaderIcon name="industry" />
                      Industry
                      {(sortBy === "industry_asc" || sortBy === "industry_desc") && (
                        sortBy === "industry_asc"
                          ? <SortAscending size={18} />
                          : <SortDescending size={18} />
                      )}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <TableHeaderIcon name="industry" />
                      Industry
                    </span>
                  )}
                </span>
              </th>
            )}
            {!isHidden("brand") && (
              <th scope="col" className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap">
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="brand" />
                  Brand
                </span>
              </th>
            )}
            {!isHidden("integrations") && (
              <th scope="col" className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden lg:table-cell whitespace-nowrap">
                <span className="flex items-center gap-1">
                  <TableHeaderIcon name="integrations" />
                  Integrations
                </span>
              </th>
            )}
            {!isHidden("tags") && (
              <th scope="col" className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden lg:table-cell whitespace-nowrap">
                <span className="flex items-center gap-1">
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() => onSortChange(sortBy === "tags_asc" ? "tags_desc" : "tags_asc")}
                    >
                      <TableHeaderIcon name="tags" />
                      Tags
                      {(sortBy === "tags_asc" || sortBy === "tags_desc") && (
                        sortBy === "tags_asc"
                          ? <SortAscending size={18} />
                          : <SortDescending size={18} />
                      )}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <TableHeaderIcon name="tags" />
                      Tags
                    </span>
                  )}
                </span>
              </th>
            )}
            {!isHidden("created") && (
              <th scope="col" className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap">
                <span className="flex items-center gap-1">
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() => onSortChange(sortBy === "created_desc" ? "created_asc" : "created_desc")}
                    >
                      <TableHeaderIcon name="created" />
                      Created
                      {(sortBy === "created_asc" || sortBy === "created_desc") && (
                        sortBy === "created_asc"
                          ? <SortAscending size={18} />
                          : <SortDescending size={18} />
                      )}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <TableHeaderIcon name="created" />
                      Created
                    </span>
                  )}
                </span>
              </th>
            )}
            {!isHidden("updated") && (
              <th scope="col" className="group text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap">
                <span className="flex items-center gap-1">
                  {onSortChange ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                      onClick={() => onSortChange(sortBy === "updated_desc" ? "updated_asc" : "updated_desc")}
                    >
                      <TableHeaderIcon name="updated" />
                      Updated
                      {(sortBy === "updated_asc" || sortBy === "updated_desc") && (
                        sortBy === "updated_asc"
                          ? <SortAscending size={18} />
                          : <SortDescending size={18} />
                      )}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <TableHeaderIcon name="updated" />
                      Updated
                    </span>
                  )}
                </span>
              </th>
            )}
            {canManageClients && (
              <th scope="col" className="py-2 px-4 w-16">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <tr
              key={client.id}
              className={`bg-background hover:bg-brand/[0.02] transition-colors${onClientClick ? " cursor-pointer" : ""}`}
              onClick={() => onClientClick?.(client)}
              onKeyDown={(e) => {
                if (onClientClick && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onClientClick(client);
                }
              }}
              tabIndex={onClientClick ? 0 : undefined}
            >
              <td className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <ClientAvatar name={client.name} logoUrl={client.logoUrl} size="sm" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {isArchived && (
                        <span className="bg-neutral-100 text-neutral-500 text-xs rounded-full px-2 py-0.5 shrink-0">
                          Archived
                        </span>
                      )}
                      <p className="font-medium truncate">{client.name}</p>
                    </div>
                  </div>
                </div>
              </td>

              {!isHidden("industry") && (
                <td className="py-3 px-4 text-foreground hidden md:table-cell whitespace-nowrap">
                  {client.industry ? (() => {
                    const Icon = getIndustryIcon(client.industry);
                    return (
                      <span className="inline-flex items-center gap-1.5">
                        {Icon && <Icon size={16} className="shrink-0 text-foreground" />}
                        {client.industry}
                      </span>
                    );
                  })() : <span className="text-foreground/30">&mdash;</span>}
                </td>
              )}

              {!isHidden("brand") && (
                <td className="py-3 px-4 text-foreground hidden md:table-cell whitespace-nowrap">
                  {client.brand ? (
                    <span className="truncate">{client.brand.name}</span>
                  ) : (
                    <span className="text-foreground/30">&mdash;</span>
                  )}
                </td>
              )}

              {!isHidden("integrations") && (
                <td className="py-3 px-4 hidden lg:table-cell">
                  {client.platforms && client.platforms.length > 0 ? (
                    <PlatformLogos platforms={client.platforms} />
                  ) : (
                    <span className="text-foreground/30">&mdash;</span>
                  )}
                </td>
              )}

              {!isHidden("tags") && (
                <td className="py-3 px-4 hidden lg:table-cell">
                  {client.tags && client.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {[...client.tags]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .slice(0, 3)
                        .map((tag) => (
                          <TagPill key={tag.id} tag={tag} size="sm" className="!text-[13px]" />
                        ))}
                      {client.tags.length > 3 && (
                        <span className="text-xs text-foreground/60 px-1.5 py-0.5">
                          +{client.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-foreground/30">&mdash;</span>
                  )}
                </td>
              )}

              {!isHidden("created") && (
                <td className="py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                  {new Date(client.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </td>
              )}

              {!isHidden("updated") && (
                <td className="py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                  {new Date(client.updatedAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </td>
              )}

              {canManageClients && (
                <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        ref={(el) => {
                          triggerRefs.current[client.id] = el;
                        }}
                        variant="ghost"
                        size="icon"
                        className="min-h-11 min-w-11 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                        aria-label={`Actions for ${client.name}`}
                      >
                        <DotsThreeVertical size={28} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-[23px] p-2 shadow-md" style={{ border: "0.5px solid var(--hairline)" }}>
                      {isArchived ? (
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onRestoreClick?.(client)}
                          disabled={restoreDisabled}
                        >
                          <ArrowCounterClockwise size={14} className="mr-1" />
                          Restore
                        </DropdownMenuItem>
                      ) : (
                        <>
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onEditClick?.(client)}
                          >
                            <PencilSimple size={14} className="mr-1" />
                            Edit Client
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onArchiveClick?.(client)}
                          >
                            <Archive size={14} className="mr-1" />
                            Archive
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                        onClick={() => onDeleteClick?.(client)}
                      >
                        <Trash size={14} className="mr-1" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
